import React, { useCallback, useEffect, useState } from 'react';
import { useThemeContext } from '../../../hook/useThemeManager';
import { ColorScheme, theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { StyleSheet } from 'react-native';
import { Box, Typography, Button, Container } from '@mui/material';
import { EsMappedFilters } from './EsMappedFilters';
import { strings } from '../../../constants';
import { DiscoveryFilter, FilterItem, mainCategorySelector } from '../../../data/filters/discovery.data';
import { FilterTab } from './FilterTab';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
    currentFilterCategorySelector,
    currentFiltersSelector,
    filterStatusSelector,
    filterSubCategoryIdSelector,
} from '../../../selectors/selectors';
import { FilterValueSet } from '../../../redux/appConfigurator/types';
import {
    setShowSearchBox,
    toggleAccountModal,
    toggleDrawer,
    toggleFiler,
    toggleNotification,
} from '../../../redux/appConfigurator/action';

interface IFilterProps {
    open?: boolean;
    width?: number | string;
    onChangeFilterCategory: (filter: FilterItem) => void;
    onChangeFilterSubCategory: (filter: FilterItem, categoryId: number) => void;
    filterList: DiscoveryFilter[];
    filterSelections: FilterValueSet;
    onClearFilterPressed: () => void;
    onApplyFilterPressed: () => void;
    disableApplyFilter?: boolean;
}

export interface IFilterRef {
    openMainCatgeory(): void;
    openSubCatgeory(): void;
}

const EsFiltersDesktop: React.FC<IFilterProps> = ({
    open,
    onChangeFilterCategory,
    onChangeFilterSubCategory,
    filterSelections,
    onClearFilterPressed,
    onApplyFilterPressed,
    disableApplyFilter,
}) => {
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);
    const [isMaincategoryOpen, setIsMainCategoryOpen] = useState<boolean>(false);
    const filterList = useSelector(currentFiltersSelector);
    const selectedCategory = useSelector(currentFilterCategorySelector);
    const filterStatus = useSelector(filterStatusSelector);
    const selectedFilterSubCategoryId = useSelector(filterSubCategoryIdSelector);
    const dispatch = useDispatch();

    const handleFilterToggle = (filter: FilterItem) => {
        onChangeFilterCategory && onChangeFilterCategory(filter);
        dispatch(
            toggleFiler({
                filterStatus: 'off',
                filterId: undefined,
            }),
        ); //TODO: Take a closer look
    };

    const handleFilterSubCategory = (filter: FilterItem, categoryId: number) => {
        onChangeFilterSubCategory(filter, categoryId);
        if (categoryId !== 8) {
            dispatch(
                toggleFiler({
                    filterStatus: isMaincategoryOpen ? 'on' : 'off',
                    filterId: selectedFilterSubCategoryId === categoryId ? undefined : categoryId,
                }),
            );
        }
        dispatch(toggleDrawer({ status: 'closed' }));
    };

    const handleFilterOpening = (filterGroupId?: number) => {
        batch(() => {
            dispatch(
                toggleFiler({
                    filterStatus: isMaincategoryOpen ? 'on' : 'off',
                    filterId: selectedFilterSubCategoryId === filterGroupId ? undefined : filterGroupId,
                }),
            );
            dispatch(toggleDrawer({ status: 'closed' }));
            dispatch(setShowSearchBox('off'));
            dispatch(toggleAccountModal({ on: false }));
            dispatch(toggleNotification({ on: false }));
        });
        setIsMainCategoryOpen(!isMaincategoryOpen);
    };

    const onFilterSubcategoryPressed = (index: number, filterGroupId?: number) => {
        setIsMainCategoryOpen(false);
        batch(() => {
            dispatch(
                toggleFiler({
                    filterStatus: filterGroupId === null ? 'on' : 'off',
                    filterId: selectedFilterSubCategoryId === filterGroupId ? undefined : filterGroupId,
                }),
            ); //TODO: Take a closer look
            dispatch(toggleDrawer({ status: 'closed' }));
            dispatch(setShowSearchBox('off'));
            dispatch(toggleAccountModal({ on: false }));
            dispatch(toggleNotification({ on: false }));
        });
    };

    const handleFilterGlobalListner = useCallback(() => {
        if (filterStatus === 'on') {
            setIsMainCategoryOpen(false);
        }
    }, [filterStatus]);

    useEffect(() => {
        handleFilterGlobalListner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus]);

    return (
        <Container sx={{ mt: 10 }}>
            <Box
                sx={{
                    height: '78px',
                    backgroundColor: theme[themeStyle].pure_black,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '15px',
                    zIndex: 999,
                }}>
                <Box
                    sx={{
                        width: '100%',
                        minWidth: '100px',
                        maxWidth: {
                            lg: '150px',
                            xl: '300px',
                        },
                    }}>
                    {mainCategorySelector.map((filter, index) => {
                        return (
                            <FilterTab
                                category={selectedCategory}
                                defaultValue={selectedCategory}
                                selectedValue={selectedCategory}
                                data={filter}
                                key={index.toString()}
                                onChange={selection => {}}
                                open={selectedFilterSubCategoryId === filter.id}
                                onFilterPressed={handleFilterOpening}
                                isSolidButton
                                onFilterSelect={selectedFilter => handleFilterToggle(selectedFilter)}
                            />
                        );
                    })}
                </Box>

                {/* <Divider
                    orientation="vertical"
                    variant="middle"
                    sx={{
                        backgroundColor: theme[themeStyle].darkGray60,
                        height: 0.5,
                        mx: 1,
                    }}
                /> */}
                <EsMappedFilters
                    onFilterPressed={(index, filterId) => onFilterSubcategoryPressed(index, filterId)}
                    filterList={filterList}
                    open={isMaincategoryOpen}
                    selectedFilterSubCategoryId={selectedFilterSubCategoryId}
                    onFilterSelect={(_, selectedFilter, categoryId) =>
                        handleFilterSubCategory(selectedFilter, categoryId)
                    }
                    filterSelections={filterSelections}
                />

                {/* <Divider
                    orientation="vertical"
                    variant="middle"
                    sx={{
                        backgroundColor: theme[themeStyle].darkGray60,
                        height: 0.5,
                        mx: 1,
                    }}
                /> */}

                {/* Filter buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'fit-content',
                        gap: '12px',
                    }}>
                    <Button
                        style={styles.mainButton}
                        onClick={onApplyFilterPressed}
                        disabled={disableApplyFilter}
                        sx={{
                            width: '120px',
                            backgroundColor: disableApplyFilter
                                ? theme[themeStyle].disabledGray
                                : theme[themeStyle].primary_orange,
                            '&:hover': { backgroundColor: theme[themeStyle].primary_white },
                        }}>
                        <Typography variant="body1" style={styles.mainText}>
                            {strings.apply_filter}
                        </Typography>
                    </Button>
                    <Button sx={{ width: '100px', textTransform: 'none' }} onClick={onClearFilterPressed}>
                        <Typography
                            variant="body1"
                            style={styles.text}
                            sx={{
                                color: theme[themeStyle].filters_white,
                                '&:hover': { color: theme[themeStyle].primary_orange },
                            }}>
                            {strings.clear_filters}
                        </Typography>
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        mainButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 100,
            height: '40px',
            paddingBottom: 10,
            paddingTop: 10,
            paddingLeft: 10,
            paddingRight: 10,
            textTransform: 'none',
        },
        text: {
            width: 'fit-content',
            textAlign: 'left',
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
        },
        mainText: {
            width: 'fit-content',
            textAlign: 'left',
            fontSize: 14,
            fontWeight: '600',
            color: theme[colorScheme].pure_black,
            fontFamily: FONT_ENUM.MAIN_BOLD,
        },
    });

export { EsFiltersDesktop };
